import { useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { customFormsApi } from 'apis/customForms';

import { CreateNewCustomFormInput, CustomForm } from '../../customForms.types';
import { Result } from '../shared.types';

import { formListConverter } from './formListConverter';
import {
  formObjectConverter,
  transformInternalTypesToApiTypes,
} from './formObjectConverter';
import { parseApiError } from './parseApiError';

type UseFormReturnType = {
  forms: CustomForm[];
  isLoading: boolean;
  getSingleForm: (formId: string) => Promise<Result<CustomForm, string>>;
  createForm: (
    input: CreateNewCustomFormInput,
  ) => Promise<Result<CustomForm, string>>;
  duplicateForm: (form: CustomForm) => Promise<Result<CustomForm, string>>;
  deleteForm: (form: CustomForm) => Promise<Result<void, string>>;
  renameForm: (
    form: CustomForm,
    newName: string,
  ) => Promise<Result<CustomForm, string>>;
  updateForm: (updatedForm: CustomForm) => Promise<Result<CustomForm, string>>;
};

export function useCustomForms(): UseFormReturnType {
  const {
    isFetching,
    isError,
    data: readFormsResponse,
  } = customFormsApi.useReadFormsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [getSingleForm, getSingleFormState] =
    customFormsApi.useLazyReadFormQuery();

  const [createForm] = customFormsApi.useCreateFormMutation();
  const [deleteForm] = customFormsApi.useDeleteFormMutation();
  const [updateForm] = customFormsApi.useUpdateFormMutation();
  const [duplicateForm] = customFormsApi.useDuplicateFormMutation();

  const getSingleFormCallback = useCallback(
    async (formId: string): Promise<Result<CustomForm, string>> => {
      try {
        const response = await getSingleForm({ form_id: formId }).unwrap();
        return {
          ok: true,
          value: formObjectConverter.toInternal(response.data),
        };
      } catch (e) {
        return { ok: false, error: parseApiError(e) };
      }
    },
    [getSingleForm],
  );

  const createFormCallback = useCallback(
    async (
      input: CreateNewCustomFormInput,
    ): Promise<Result<CustomForm, string>> => {
      try {
        const response = await createForm(input).unwrap();
        return {
          ok: true,
          value: formObjectConverter.toInternal(response.data),
        };
      } catch (e) {
        return { ok: false, error: parseApiError(e) };
      }
    },
    [createForm],
  );

  const deleteFormCallback = useCallback(
    async (form: CustomForm): Promise<Result<void, string>> => {
      try {
        await deleteForm({ form_id: form.id }).unwrap();
        return { ok: true, value: undefined };
      } catch (e) {
        return { ok: false, error: parseApiError(e) };
      }
    },
    [deleteForm],
  );

  const duplicateFormCallback = useCallback(
    async (form: CustomForm): Promise<Result<CustomForm, string>> => {
      try {
        const response = await duplicateForm({
          form_id: form.id,
          identifier: uuid(),
        }).unwrap();
        return {
          ok: true,
          value: formObjectConverter.toInternal(response.data),
        };
      } catch (e) {
        return { ok: false, error: parseApiError(e) };
      }
    },
    [duplicateForm],
  );

  const renameFormCallback = useCallback(
    async (
      form: CustomForm,
      newName: string,
    ): Promise<Result<CustomForm, string>> => {
      try {
        // First fetch the details of the custom form
        const response = await getSingleForm({ form_id: form.id }).unwrap();
        const detailedForm = response.data;

        // Then update the label, and keep the rest of the details the same
        await updateForm({ ...detailedForm, label: newName }).unwrap();

        return {
          ok: true,
          value: formObjectConverter.toInternal(detailedForm),
        };
      } catch (error) {
        return { ok: false, error: parseApiError(error) };
      }
    },
    [getSingleForm, updateForm],
  );

  const updateFormCallback = useCallback(
    async (updatedForm: CustomForm): Promise<Result<CustomForm, string>> => {
      try {
        const response = await updateForm({
          ...transformInternalTypesToApiTypes(updatedForm),
        }).unwrap();

        return {
          ok: true,
          value: formObjectConverter.toInternal(response.data),
        };
      } catch (e) {
        return { ok: false, error: parseApiError(e) };
      }
    },
    [updateForm],
  );

  return useMemo(
    () => ({
      isLoading: isFetching || getSingleFormState.isLoading,
      forms:
        isError || !readFormsResponse?.data
          ? []
          : formListConverter.toInternal(readFormsResponse.data.forms) ?? [],
      getSingleForm: getSingleFormCallback,
      createForm: createFormCallback,
      deleteForm: deleteFormCallback,
      duplicateForm: duplicateFormCallback,
      renameForm: renameFormCallback,
      updateForm: updateFormCallback,
    }),
    [
      createFormCallback,
      readFormsResponse?.data,
      deleteFormCallback,
      duplicateFormCallback,
      getSingleFormCallback,
      getSingleFormState.isLoading,
      isError,
      isFetching,
      renameFormCallback,
      updateFormCallback,
    ],
  );
}
